import { logger } from "./logger";
/**
 * FuturesFury Analytics — Lightweight GTM-compatible event system.
 *
 * Fires to window.dataLayer (for GTM / GA4 forwarding).
 * Forwards to PostHog when available.
 * Logs to console in development for QA verification.
 * No vendor lock-in — extend by adding forwarding adapters later.
 */

const IS_DEV = process.env.NODE_ENV === "development";

// Events we mirror to our own backend (powers the live Trust Strip).
// Kept tiny on purpose so we don't double-up on every event.
const SERVER_MIRROR_EVENTS = new Set(["quiz_completed", "calculator_result_viewed"]);

function ensureDataLayer() {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
  }
}

function _mirrorToBackend(eventName, properties) {
  if (typeof window === "undefined" || !SERVER_MIRROR_EVENTS.has(eventName)) return;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  if (!apiBase) return;
  const url = `${apiBase}/api/analytics/usage-event`;
  const body = JSON.stringify({ name: eventName, props: properties });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch (_e) {
    /* fire-and-forget — never block UX */
  }
}

/**
 * Push a named event with arbitrary properties.
 * @param {string} eventName  — snake_case event name
 * @param {object} properties — key/value pairs
 */
export function trackEvent(eventName, properties = {}) {
  ensureDataLayer();
  const payload = {
    event: eventName,
    ...properties,
    timestamp: new Date().toISOString(),
  };

  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(payload);
  }

  // Forward to PostHog if loaded
  if (typeof window !== "undefined" && window.posthog) {
    try { window.posthog.capture(eventName, properties); } catch (err) { logger.error('[analytics] PostHog capture failed:', err); }
  }

  // Mirror specific high-value events to our backend (powers Trust Strip)
  _mirrorToBackend(eventName, properties);

  if (IS_DEV) {
    logger.debug(`[FF Analytics] ${eventName}`, properties);
  }
}

/* ────────────────────────────────────────────
   Pre-defined event helpers (consistent naming)
   ──────────────────────────────────────────── */

/** User landed on /signup */
export const trackSignupPageView = () =>
  trackEvent("signup_page_view");

/** User started filling the form (first field focus) */
export const trackSignupStarted = (method = "email") =>
  trackEvent("signup_started", { method });

/** Signup succeeded */
export const trackSignupSuccess = (method = "email") =>
  trackEvent("signup_success", { method });

/** Quick Start screen viewed */
export const trackQuickStartViewed = () =>
  trackEvent("quickstart_viewed");

/** User selected a firm during Quick Start */
export const trackQuickStartFirmSelected = (firmName, firmSlug) =>
  trackEvent("quickstart_firm_selected", { firm_name: firmName, firm_slug: firmSlug });

/** Quick Start completed (button clicked) */
export const trackQuickStartCompleted = (firmCount, alertsEnabled) =>
  trackEvent("quickstart_completed", { firms_selected: firmCount, alerts_enabled: alertsEnabled });

/** User opened a tool */
export const trackToolOpened = (toolName) =>
  trackEvent("tool_opened", { tool_name: toolName });

/** Demo row created in Profit Tracker */
export const trackDemoRowCreated = () =>
  trackEvent("profit_tracker_demo_row_created");

/** Demo row removed */
export const trackDemoRowRemoved = () =>
  trackEvent("profit_tracker_demo_row_removed");

/** First real (non-demo) entry added */
export const trackFirstRealEntry = (firmName) =>
  trackEvent("profit_tracker_first_real_entry", { firm_name: firmName });

/* ────────────────────────────────────────────
   Firm Top Summary events
   ──────────────────────────────────────────── */

/** FirmTopSummary entered viewport (fire once per page) */
export const trackFirmSummaryView = (firm, ffScoreTotal) =>
  trackEvent("firm_top_summary_view", {
    firm_slug: firm.slug,
    firm_name: firm.name,
    ff_score_total: ffScoreTotal,
    drawdown_type: firm.drawdown_type || null,
    payout_frequency: firm.payout_frequency || null,
    has_activation_fee: !!(firm.activation_fee && firm.activation_fee !== "$0" && firm.activation_fee.toLowerCase() !== "none"),
    has_consistency_rule: !!(firm.consistency_rule && !firm.consistency_rule.toLowerCase().includes("none")),
  });

/** User expanded "Why this score?" */
export const trackScoreExpand = (firmSlug, firmName, ffScoreTotal) =>
  trackEvent("firm_top_summary_score_expand", {
    firm_slug: firmSlug,
    firm_name: firmName,
    ff_score_total: ffScoreTotal,
  });

/** User collapsed "Why this score?" */
export const trackScoreCollapse = (firmSlug, firmName, ffScoreTotal) =>
  trackEvent("firm_top_summary_score_collapse", {
    firm_slug: firmSlug,
    firm_name: firmName,
    ff_score_total: ffScoreTotal,
  });
